import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateClass } from '@/features/classes/hooks';
import type { ClassView } from '@/features/classes/types';
import { ApiError } from '@/lib/api/client';

/** `PATCH /classes/:id` only accepts `name`/`academicYear` - the learning space cannot be changed after creation. */
export function EditClassDialog({
  classView,
  open,
  onOpenChange,
}: {
  readonly classView: ClassView;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [name, setName] = useState(classView.name);
  const [academicYear, setAcademicYear] = useState(classView.academicYear ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const update = useUpdateClass(classView.id);

  useEffect(() => {
    if (open) {
      setName(classView.name);
      setAcademicYear(classView.academicYear ?? '');
      setValidationError(null);
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classView.id]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (name.trim().length === 0) {
      setValidationError('Nama kelas tidak boleh kosong.');
      return;
    }

    update.mutate(
      {
        name: name.trim(),
        academicYear: academicYear.trim().length > 0 ? academicYear.trim() : null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    update.error instanceof ApiError
      ? update.error.message
      : update.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ubah Kelas</DialogTitle>
          <DialogDescription>Perbarui nama atau tahun ajaran kelas ini.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-class-name">Nama Kelas</Label>
            <Input
              id="edit-class-name"
              type="text"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-class-academic-year">Tahun Ajaran</Label>
            <Input
              id="edit-class-academic-year"
              type="text"
              value={academicYear}
              onChange={(event) => {
                setAcademicYear(event.target.value);
              }}
              placeholder="2026/2027"
              autoComplete="off"
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
